import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TemplateFolder } from '../models/TemplateFolder';
import { TemplateVersion } from '../models/TemplateVersion';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TemplatesService {
  private baseUrl = environment.microservices.template;
  private contractBaseUrl = environment.microservices.contract; // URL do serviço de contratos para endpoints de templates

  constructor(private http: HttpClient, private errorService: ErrorService) {}

  listFolders(): Observable<TemplateFolder[]> {
    // Usar contractBaseUrl
    const url = `${this.contractBaseUrl}/list/templates`;
    return this.http.get<any[]>(url).pipe(
      map(list => (list || []).map(dto => this.mapFromDto(dto))),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao listar pastas de templates'); return throwError(() => err); })
    );
  }

  private mapFromDto(dto: any): TemplateFolder {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      name: pick(dto, ['Name', 'name']) ?? '',
      area: pick(dto, ['Area', 'area']) ?? '',
      observation: pick(dto, ['Observation', 'observation']) ?? null,
      isActive: pick(dto, ['IsActive', 'isActive']) ?? true,
      createdAt: pick(dto, ['CreatedAt', 'createdAt']) ?? null,
      updatedAt: pick(dto, ['UpdatedAt', 'updatedAt']) ?? null,
      createdBy: pick(dto, ['CreatedBy', 'createdBy']) ?? null,
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']) ?? null
    };
  }

  listVersions(templateId: number): Observable<TemplateVersion[]> {
    const url = `${this.contractBaseUrl}/list/contract-templates/${templateId}/versions`;
    return this.http.get<any[]>(url).pipe(
      map(list => (list || []).map(dto => this.mapVersionFromDto(dto))),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao listar versões do template'); return throwError(() => err); })
    );
  }

  private mapVersionFromDto(dto: any): TemplateVersion {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      documentTemplateId: pick(dto, ['DocumentTemplateId', 'documentTemplateId']) ?? 0,
      versionNumber: pick(dto, ['VersionNumber', 'versionNumber']) ?? 0,
      status: pick(dto, ['Status', 'status']) ?? '',
      storageKey: pick(dto, ['StorageKey', 'storageKey']) ?? '',
      storageUrl: pick(dto, ['StorageUrl', 'storageUrl']) ?? '',
      mimeType: pick(dto, ['MimeType', 'mimeType']) ?? 'application/octet-stream',
      fileHash: pick(dto, ['FileHash', 'fileHash']) ?? null,
      fileSizeBytes: pick(dto, ['FileSizeBytes', 'fileSizeBytes']) ?? null,
      placeholdersSchemaJson: pick(dto, ['PlaceholdersSchemaJson', 'placeholdersSchemaJson']) ?? null,
      createdAt: pick(dto, ['CreatedAt', 'createdAt']) ?? null,
      updatedAt: pick(dto, ['UpdatedAt', 'updatedAt']) ?? null,
      createdBy: pick(dto, ['CreatedBy', 'createdBy']) ?? null,
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']) ?? null
    };
  }

  uploadDocx(templateId: number, file: File, storageKey?: string): Observable<TemplateVersion> {
    if (!file || file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return throwError(() => new Error('Selecione um arquivo .docx válido.'));
    }

    const formData = new FormData();
    formData.append('file', file, file.name);
    if (storageKey) {
      formData.append('key', storageKey);
    }
    const headers = new HttpHeaders();

    return this.http.post<any>(`${this.baseUrl}/upload/contract-templates/${templateId}`, formData, { headers }).pipe(
      map(dto => this.mapVersionFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao enviar template'); return throwError(() => err); })
    );
  }

  downloadVersion(versionId: number): Observable<{ blob: Blob; fileName: string; mimeType: string }> {
    // Endpoint com código de autenticação quando necessário (Azure Functions)
    // Padrão: https://erp-dev-contract-*.azurewebsites.net/api/download/contract-templates/versions/{versionId}
    const url = `${this.contractBaseUrl}/download/contract-templates/versions/${versionId}`;
    
    
    // Headers para evitar problemas de CORS
    const headers = new HttpHeaders({
      'Accept': 'application/octet-stream, application/vnd.openxmlformats-officedocument.wordprocessingml.document, */*'
    });
    
    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
      headers: headers
    }).pipe(
      switchMap(async (res: HttpResponse<Blob>) => {
        const status = res.status;
        const blob = res.body as Blob;
        const contentType = res.headers.get('Content-Type') || res.headers.get('content-type') || '';
        
        
        // Verificar se o status indica erro (404, 500, etc)
        if (status !== 200 && status !== 201) {
          const text = await blob.text();
          let errorMessage = 'Erro ao baixar arquivo.';
          try {
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.error || errorJson.message || errorMessage;
          } catch {
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        // Validar se o blob é válido
        if (!blob || blob.size === 0) {
          throw new Error('O arquivo baixado está vazio ou corrompido.');
        }
        
        // Verificar se a resposta é JSON (erro do servidor mesmo com status 200)
        if (contentType.includes('application/json') || contentType.includes('text/json')) {
          const text = await blob.text();
          let errorMessage = 'Erro ao baixar arquivo.';
          try {
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.error || errorJson.message || errorMessage;
            console.error('❌ Erro do servidor (JSON):', errorMessage);
            
            // Mensagem mais específica para arquivo não encontrado
            if (errorMessage.includes('não encontrado') || errorMessage.includes('not found')) {
              errorMessage = `Arquivo não encontrado no storage. Verifique se a versão ${versionId} existe e se o arquivo foi enviado corretamente.`;
            }
          } catch {
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        // Verificar se o arquivo começa com JSON (erro retornado como blob)
        const firstBytes = await blob.slice(0, 1).text();
        if (firstBytes === '{' || firstBytes === '[') {
          const text = await blob.text();
          let errorMessage = 'Erro ao baixar arquivo.';
          try {
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.error || errorJson.message || errorMessage;
            console.error('❌ Erro do servidor (Blob com JSON):', errorMessage);
            
            // Mensagem mais específica para arquivo não encontrado
            if (errorMessage.includes('não encontrado') || errorMessage.includes('not found')) {
              errorMessage = `Arquivo não encontrado no storage. Verifique se a versão ${versionId} existe e se o arquivo foi enviado corretamente.`;
            }
          } catch {
            errorMessage = text.substring(0, 200) || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        const contentDisposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition') || '';
        const fileName = this.extractFileNameFromContentDisposition(contentDisposition) || `template_v${versionId}.docx`;
        const mimeType = contentType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        
        return { blob, fileName, mimeType };
      }),
      catchError(err => { 
        console.error('Erro ao baixar versão:', err);
        this.errorService.handleHttpError(err, 'Erro ao baixar versão do template'); 
        return throwError(() => err); 
      })
    );
  }

  createFolder(folderData: { name: string; area: string; observation?: string; isActive: boolean }): Observable<TemplateFolder> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    return this.http.post<any>(`${this.baseUrl}/create/templates`, folderData, { headers }).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao criar pasta de template'); return throwError(() => err); })
    );
  }

  updateFolder(id: number, folderData: { name: string; area: string; observation?: string; isActive: boolean }): Observable<TemplateFolder> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put<any>(`${this.baseUrl}/edit/contract-templates/${id}`, folderData, { headers }).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar pasta de template'); return throwError(() => err); })
    );
  }

  getFolder(id: number): Observable<TemplateFolder> {
    return this.http.get<any>(`${this.baseUrl}/view/templates/${id}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar pasta de template'); return throwError(() => err); })
    );
  }

  viewTemplate(templateId: number): Observable<{ blob: Blob; fileName: string; mimeType: string }> {
    return this.http.get(`${this.baseUrl}/view/templates/${templateId}`, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      switchMap(async (res: HttpResponse<Blob>) => {
        const blob = res.body as Blob;
        
        // Validar se o blob é válido
        if (!blob || blob.size === 0) {
          throw new Error('O arquivo baixado está vazio ou corrompido.');
        }
        
        // Verificar se o tipo de conteúdo está correto
        const contentType = res.headers.get('Content-Type') || res.headers.get('content-type') || '';
        
        // Verificar se a resposta é JSON (erro do servidor)
        if (contentType.includes('application/json') || contentType.includes('text/json')) {
          const text = await blob.text();
          let errorMessage = 'Erro ao visualizar template.';
          try {
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        // Verificar se o arquivo começa com JSON (erro retornado como blob)
        const firstBytes = await blob.slice(0, 1).text();
        if (firstBytes === '{' || firstBytes === '[') {
          const text = await blob.text();
          let errorMessage = 'Erro ao visualizar template.';
          try {
            const errorJson = JSON.parse(text);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            errorMessage = text.substring(0, 200) || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        const contentDisposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition') || '';
        const fileName = this.extractFileNameFromContentDisposition(contentDisposition) || `template_${templateId}.docx`;
        const mimeType = contentType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        
        return { blob, fileName, mimeType };
      }),
      catchError(err => { 
        console.error('Erro ao visualizar template:', err);
        this.errorService.handleHttpError(err, 'Erro ao visualizar template'); 
        return throwError(() => err); 
      })
    );
  }

  deleteFolder(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete/templates/${id}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao excluir pasta de template'); return throwError(() => err); })
    );
  }

  private extractFileNameFromContentDisposition(cd: string): string | null {
    if (!cd) return null;
    // Examples: attachment; filename="CONTRATO.docx" OR form-data; name="file"; filename="CONTRATO.docx"
    const fileNameMatch = cd.match(/filename\*?=([^;]+)/i);
    if (fileNameMatch && fileNameMatch[1]) {
      const raw = fileNameMatch[1].trim().replace(/^UTF-8''/i, '').replace(/"/g, '');
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
    return null;
  }
}


